import { useAuth0 } from "@auth0/auth0-react";
import { useState, useEffect } from "react";
import { Dialog, Box, TextField, Stack, MenuItem, Autocomplete, Checkbox, FormControlLabel, Switch, Tabs, Tab, Button, Typography, Alert, Tooltip } from "@mui/material";
import type { DialogProps } from "@mui/material";
import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import { IMeeting, MeetingStatus, IProject, IAdvisor, IUser } from "../../../../types";
import { createMeeting, refreshMeetgeek, updateMeeting } from "../../services/gutcheck-admin-api.service";
import { MeetingDetailsTab } from "./MeetingDetaillsTab";
import { RefreshCw, TriangleAlert } from "lucide-react";
import { theme } from "../../../../theme/theme";

dayjs.extend(utc);

const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 600,
    bgcolor: 'background.paper',
    border: '1px solid #c9c9c9',
    boxShadow: 24,
    p: 4,
    borderRadius: '10px',
    width: '50%'
};

interface MeetingSettingsTabProps {
    meeting?: IMeeting;
    onSave: () => Promise<void>;
    onCancel: () => void;
}

export const MeetingSettingsTab = ({
    meeting,
    onSave,
    onCancel
}: MeetingSettingsTabProps) => {
    const { getAccessTokenSilently } = useAuth0();
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
    const [refreshingMeetgeek, setRefreshingMeetgeek] = useState(false);

    const initialMeeting = {
        project: meeting?.project ?? null,
        advisor: meeting?.advisor ?? null,
        description: meeting?.description ?? "",
        status: meeting?.status ?? MeetingStatus.REQUESTED,
        event_start: meeting?.event_start ?
            dayjs(meeting.event_start).format('YYYY-MM-DDTHH:mm') :
            dayjs().format('YYYY-MM-DDTHH:mm'),
        event_duration: meeting?.event_duration ? meeting.event_duration / 60000 : 30,
        requested: meeting?.requested ?? true,
        requested_at: meeting?.requested_at ?? new Date(),
        request_note: meeting?.request_note ?? "",
        on_hold: meeting?.on_hold ?? false,
        fireflies_id: meeting?.fireflies_id ?? "",
        meetgeek_id: meeting?.meetgeek_id ?? "",
        override_calendly: meeting?.override_calendly ?? false,
        calendly_invitee_uri: meeting?.calendly_invitee_uri ?? "",
        calendly_host_name: meeting?.calendly_host_name ?? "",
    };

    useEffect(() => {
        setMeetingForm(initialMeeting);
    }, [meeting]);

    const [meetingForm, setMeetingForm] = useState(initialMeeting);

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        setIsSaving(true);
        setSubmitError(null);
        setSubmitSuccess(null);

        const accessToken = await getAccessTokenSilently();

        const newMeeting = {
            ...meetingForm,
            event_start: new Date(meetingForm.event_start).toISOString(),
            requested_at: new Date().toISOString(),
            event_duration: Number(meetingForm.event_duration) * 60000,
        };

        try {
            if (meeting?.id) {
                await updateMeeting(meeting.uuid, newMeeting, accessToken);
            } else {
                await createMeeting(newMeeting, accessToken);
            }
            setMeetingForm(initialMeeting);
            await onSave();
            setSubmitSuccess("Meeting saved successfully");
        } catch (error) {
            console.error('Error saving meeting:', error);
            setSubmitError('Failed to save meeting. Please try again.');
        } finally {
            setIsSaving(false);
        }
    };

    const handleChange = (event: any) => {
        const { name, value } = event.target;

        setMeetingForm({ ...meetingForm, [name]: value });
    };

    const handleRefreshMeetgeek = async () => {
        setRefreshingMeetgeek(true);
        setIsSaving(true);
        setSubmitError(null);
        setSubmitSuccess(null);
        const accessToken = await getAccessTokenSilently();
        try {
            await refreshMeetgeek(meeting?.uuid ?? "", accessToken);
            setRefreshingMeetgeek(false);
            setSubmitSuccess("Meetgeek refreshed successfully");
            await onSave();
        } catch (e) {
            setRefreshingMeetgeek(false);
            setSubmitError("Error refreshing Meetgeek. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };


    return (
        <Box p={2}>


            <form onSubmit={handleSubmit}>
                <Stack spacing={2}>
                    <TextField
                        name="project"
                        label="Company"
                        value={meetingForm.project?.name}
                        required
                        disabled={true}
                    />


                    <TextField
                        name="advisor"
                        label="Advisor"
                        value={meetingForm.advisor?.user?.profile?.name}
                        required
                        disabled={true}
                    />

                    <TextField
                        name="calendly_host_name"
                        label="Calendly Host Name"
                        value={meetingForm.calendly_host_name}
                        onChange={handleChange}
                    />

                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            label="Calendly Invitee URI"
                            value={meetingForm.calendly_invitee_uri}
                            onChange={handleChange}
                        />
                        <Stack direction="column" alignItems="center" sx={{ ml: 2 }}>
                            <span style={{ fontSize: '0.9rem', marginBottom: 2 }}>Override</span>
                            <Switch
                                name="override_calendly"
                                checked={meetingForm.override_calendly}
                                onChange={e => setMeetingForm({ ...meetingForm, override_calendly: e.target.checked })}
                            />
                        </Stack>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                            select
                            name="status"
                            label="Status"
                            value={meetingForm.status}
                            onChange={handleChange}
                            required
                        >
                            {Object.values(MeetingStatus).map((status) => (
                                <MenuItem key={status} value={status}>
                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Stack direction="column" alignItems="center" sx={{ ml: 2 }}>
                            <span style={{ fontSize: '0.9rem', marginBottom: 2 }}>On Hold</span>
                            <Switch
                                name="on_hold"
                                checked={meetingForm.on_hold}
                                onChange={e => setMeetingForm({ ...meetingForm, on_hold: e.target.checked })}
                            />
                        </Stack>
                    </Stack>

                    <TextField
                        type="datetime-local"
                        name="event_start"
                        label="Start Time"
                        InputLabelProps={{ shrink: true }}
                        onChange={handleChange}
                        value={meetingForm.event_start}
                        required
                    />

                    <TextField
                        type="number"
                        name="event_duration"
                        label="Duration (minutes)"
                        value={meetingForm.event_duration}
                        onChange={handleChange}
                        required
                    />

                    <TextField
                        name="description"
                        label="Description"
                        value={meetingForm.description}
                        onChange={handleChange}
                    />

                    <Stack direction="column" spacing={1}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <TextField
                                name="meetgeek_id"
                                label="Meetgeek ID"
                                value={meetingForm.meetgeek_id}
                                onChange={handleChange}
                            />
                            {meeting?.meetgeek_id && (
                                <Tooltip title="Refresh Meetgeek">
                                    <RefreshCw onClick={handleRefreshMeetgeek} size={20} color={theme.palette.primary.main} style={{ cursor: 'pointer' }} />
                                </Tooltip>
                            )}

                        </Stack>
                        {meeting?.meetgeek_id && (
                            <Typography variant="caption" color="text.secondary">
                                <TriangleAlert size={16} color={theme.palette.warning.main} style={{ marginRight: 4 }} /> Clicking refresh the first time will trigger a meeting recap email to be sent to the participants.
                            </Typography>
                        )}
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="flex-end">
                        {submitError && <Typography color="error" variant="body1">{submitError}</Typography>}
                        {submitSuccess && <Typography color="success" variant="body1">{submitSuccess}</Typography>}
                        <Button type="submit" variant="contained" color="primary" disabled={isSaving}>
                            {isSaving ? 'Saving...' : 'Save'}
                        </Button>
                    </Stack>
                </Stack>
            </form>
        </Box>
    );
}; 